import { useEffect, useMemo } from 'react';

import { Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';

import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { goalsSelectors } from 'app-components/flux/goals';
import { ADD_GOAL_DIALOG_TYPE } from 'components/Dialogs/Goals/actions';
import { goalTypes } from 'components/Dialogs/Goals/AddGoal/state';
import { EditGoalAction } from 'containers/Goals/EditGoal/actions';

export const DIALOG_TYPE_LLM_GOAL = 'LLM_GOAL';

type PrefillGoalData = {
  id?: string;
  name?: string;
  type?: string;
  targetAmount?: number;
  savedSoFar?: number;
  contributionAmount?: number;
  startOn?: string;
  targetOn?: string;
  isTakenFromF2S?: boolean;
};

type LlmGoalDialogProps = {
  dialogId: string;
  operation: 'create' | 'update';
  data: PrefillGoalData;
  callback: (result: { success: boolean; goalId?: string; error?: string }) => void;
  context?: string;
};

const LlmGoalDialog = ({ dialogId, operation, data, callback, context = 'llm-goal' }: LlmGoalDialogProps) => {
  const dispatch = useAppDispatch();
  const existingGoal = useAppSelector((state) => (data?.id ? goalsSelectors.getGoalById(state, data.id) : undefined));

  const goalType = useMemo(
    () => goalTypes.find((g) => g.type === data.type) ?? goalTypes[goalTypes.length - 1],
    [data.type],
  );

  const prefill = useMemo(() => {
    const startDate = data.startOn ? DateTime.fromISO(data.startOn) : null;
    const targetDate = data.targetOn ? DateTime.fromISO(data.targetOn) : null;

    return {
      targetAmount: data.targetAmount,
      contributionAmount: data.contributionAmount,
      savedSoFar: data.savedSoFar,
      startDate: startDate?.isValid ? startDate : undefined,
      targetDate: targetDate?.isValid ? targetDate : undefined,
      includeInF2S: data.isTakenFromF2S,
    };
  }, [data.contributionAmount, data.isTakenFromF2S, data.savedSoFar, data.startOn, data.targetAmount, data.targetOn]);

  useEffect(() => {
    if (operation === 'update') {
      if (!data.id) {
        callback({ success: false, error: 'Goal updates require an id.' });
        dispatch(rootUiActions.removeDialog(dialogId));
        return;
      }

      const overrides: Record<string, unknown> = {};
      if (data.name !== undefined) overrides.name = data.name;
      if (data.type !== undefined) overrides.type = data.type;
      if (data.targetAmount !== undefined) overrides.targetAmount = data.targetAmount;
      if (data.targetOn !== undefined) overrides.targetOn = data.targetOn;
      if (data.startOn !== undefined) overrides.startOn = data.startOn;
      if (data.contributionAmount !== undefined) overrides.contributionAmount = data.contributionAmount;
      if (typeof data.isTakenFromF2S === 'boolean') overrides.isTakenFromF2S = data.isTakenFromF2S;
      if (data.savedSoFar !== undefined) overrides.savedSoFar = data.savedSoFar;

      const mergedGoal = existingGoal ? (existingGoal as any).merge(overrides) : ({ id: data.id, ...overrides } as any);

      dispatch(
        EditGoalAction({
          goal: mergedGoal,
          context: context ?? 'llm-goal',
          callback,
        }),
      );
      dispatch(rootUiActions.removeDialog(dialogId));
      return;
    }

    const addGoalDialogId = `${ADD_GOAL_DIALOG_TYPE}_${dialogId}`;

    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: addGoalDialogId,
          type: ADD_GOAL_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap({
            context,
            name: data.name ?? '',
            type: goalType,
            prefill,
            onResult: callback,
          }),
        }),
      ),
    );

    dispatch(rootUiActions.removeDialog(dialogId));
  }, [
    callback,
    context,
    data.contributionAmount,
    data.id,
    data.isTakenFromF2S,
    data.name,
    data.savedSoFar,
    data.startOn,
    data.targetAmount,
    data.targetOn,
    data.type,
    dialogId,
    dispatch,
    existingGoal,
    goalType,
    operation,
    prefill,
  ]);
  return null;
};

export default LlmGoalDialog;
